/**
 * Stunt 3 — "Honest Read" panel.
 *
 * Reuses Stunt 2 data (PPR median sale in the listing's Eircode) to give
 * a plain-English verdict on whether the asking price is realistic,
 * plus a suggested fair offer. Same admin kill-switch as Stunt 2
 * (`eircode_pages_enabled` flag) — when OFF the API returns 404 and
 * this component renders nothing.
 *
 * Props:
 *   propertyId   — string. Required.
 *
 * Mounts on PropertyDetailPage.jsx near the price/negotiation cluster.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Eye, TrendingUp, TrendingDown, Minus, ArrowRight, Loader2,
  CheckCircle2, AlertTriangle, ShieldCheck, BellRing, X,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import DataQualityBadge from './DataQualityBadge';
import { useMarket } from '../hooks/useMarket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Render a number in compact form using the active market's currency
 *  symbol. €/£/$ — same shape, market-driven. */
const compactMoney = (n, symbol) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1_000_000) return `${symbol}${(n / 1_000_000).toFixed(2)}m`;
  if (Math.abs(n) >= 1_000) return `${symbol}${Math.round(n / 1000)}k`;
  return `${symbol}${Math.round(n)}`;
};

// Backwards-compat alias — many internal call sites still expect the IE-
// hardcoded helper. Removed once every call site uses the local
// `compactMoney(value, symbol)` form.
const eurK = (n) => compactMoney(n, '€');

const TONE_STYLES = {
  good: {
    border: 'border-emerald-300',
    bg: 'from-emerald-50 to-white',
    chipBg: 'bg-emerald-100 text-emerald-900',
    accent: 'text-emerald-700',
    icon: ShieldCheck,
  },
  watch: {
    border: 'border-amber-300',
    bg: 'from-amber-50 to-white',
    chipBg: 'bg-amber-100 text-amber-900',
    accent: 'text-amber-700',
    icon: AlertTriangle,
  },
  bad: {
    border: 'border-red-300',
    bg: 'from-red-50 to-white',
    chipBg: 'bg-red-100 text-red-900',
    accent: 'text-red-700',
    icon: AlertTriangle,
  },
  neutral: {
    border: 'border-gray-200',
    bg: 'from-gray-50 to-white',
    chipBg: 'bg-gray-100 text-gray-700',
    accent: 'text-gray-700',
    icon: Eye,
  },
};

const VERDICT_LABEL = {
  below_market: 'Priced below market',
  in_line: 'Priced in line with the area',
  above_market: 'Room to negotiate',
  stretch: 'Ambitious ask',
  unknown: 'Not enough data',
};

const PriceWatchInline = ({ propertyId }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/price-watch`,
        { property_id: propertyId, email, source: 'honest-read' });
      setDone(true);
      toast({ title: "You're watching this listing",
              description: "We'll email you the day the price changes — up or down." });
    } catch (err) {
      toast({ title: 'Could not subscribe',
              description: err.response?.data?.detail || err.message,
              variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mt-5 pt-5 border-t border-white/60 flex items-center gap-2 text-sm text-emerald-900 font-medium bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="price-watch-success">
        <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
        <span>You're watching this listing — we'll email you when the seller drops the price.</span>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-5 pt-5 border-t border-white/60 w-full flex items-center justify-between gap-2 text-left group"
        data-testid="price-watch-cta-open"
      >
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F5EFFF] text-[#5b35a8] group-hover:bg-[#7042cc] group-hover:text-white transition-colors">
            <BellRing className="h-4 w-4" />
          </span>
          <div>
            <div className="text-sm font-semibold text-gray-900">Track price changes</div>
            <div className="text-xs text-gray-600">
              We&apos;ll email you the day the price changes — up or down.
            </div>
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-[#7042cc] group-hover:translate-x-0.5 transition-all" />
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-5 pt-5 border-t border-white/60 bg-white/60 rounded-lg p-3"
      data-testid="price-watch-form"
    >
      <div className="flex items-center justify-between mb-2">
        <Label htmlFor="pw-email" className="text-xs font-semibold text-gray-900">
          Email me when this price changes
        </Label>
        <button type="button" onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-gray-700"
                data-testid="price-watch-close"
                aria-label="Close">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex gap-2">
        <Input
          id="pw-email"
          type="email"
          required
          autoFocus
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 h-9 text-sm"
          data-testid="price-watch-email-input"
        />
        <Button
          type="submit"
          disabled={busy}
          size="sm"
          className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
          data-testid="price-watch-submit"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Watch'}
        </Button>
      </div>
      <p className="text-[10px] text-gray-500 mt-2">
        One email per price change. Cancel any time with one click.
      </p>
    </form>
  );
};

const HonestReadPanel = ({ propertyId, country }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // useMarket() picks up the active country — used for DataQualityBadge
  // routing only here; backend /api/honest-read already returns the right
  // currency-tagged numbers based on the listing's country.
  const market = useMarket(country);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/honest-read/${propertyId}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* 404 → silently hide (flag OFF or no data) */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [propertyId]);

  // Loading: very light skeleton so we don't blink-flash the page.
  if (loading) {
    return (
      <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 flex items-center gap-2 text-xs text-gray-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading Honest Read…
      </div>
    );
  }

  // Flag off / no data / rent listing → render nothing.
  if (!data) return null;

  const tone = TONE_STYLES[data.tone] || TONE_STYLES.neutral;
  const ToneIcon = tone.icon;
  const verdictLabel = VERDICT_LABEL[data.verdict] || 'Honest Read';
  const deltaPositive = (data.delta_pct ?? 0) > 0;
  const deltaSign = deltaPositive ? '+' : '';
  const deltaIcon = data.delta_pct == null
    ? Minus
    : data.delta_pct > 5
      ? TrendingUp
      : data.delta_pct < -5
        ? TrendingDown
        : Minus;
  const DeltaIcon = deltaIcon;

  // Market-aware compact money — UK panels render £, IE panels €, US $.
  // The market resolves from the property's country (passed via prop)
  // so a UK listing rendered inside an IE-buyer's session still gets £.
  const moneyK = (n) => compactMoney(n, market.currency_symbol);

  return (
    <Card
      className={`border-2 ${tone.border} bg-gradient-to-br ${tone.bg} overflow-hidden`}
      data-testid="honest-read-panel"
    >
      <CardContent className="p-5 sm:p-6">
        {/* Eyebrow */}
        <div className={`inline-flex items-center gap-2 ${tone.chipBg} rounded-full px-3 py-1 text-[10px] uppercase tracking-widest font-semibold mb-3`}>
          <Eye className="h-3 w-3" />
          Honest Read
        </div>

        {/* Headline */}
        <div className="flex items-start gap-3 mb-4">
          <div className={`flex-shrink-0 rounded-full p-2 ${tone.chipBg}`}>
            <ToneIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 leading-snug" data-testid="honest-read-verdict">
              {verdictLabel}
            </h3>
            <p className="text-sm text-gray-700 mt-1 leading-relaxed" data-testid="honest-read-summary">
              {data.summary}
            </p>
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-white/60">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1">
              Asking
            </div>
            <div className="text-xl font-bold text-gray-900 tabular-nums" data-testid="honest-read-asking">
              {moneyK(data.asking_price)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1">
              {data.routing_key} median sale
            </div>
            <div className="text-xl font-bold text-gray-900 tabular-nums" data-testid="honest-read-median">
              {moneyK(data.median_sale_12m)}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              from {data.comparable_sales_count} sale{data.comparable_sales_count === 1 ? '' : 's'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1">
              Delta
            </div>
            <div
              className={`text-xl font-bold tabular-nums ${tone.accent} flex items-center gap-1`}
              data-testid="honest-read-delta"
            >
              <DeltaIcon className="h-4 w-4" />
              {data.delta_pct != null ? `${deltaSign}${data.delta_pct}%` : '—'}
            </div>
            {data.yoy_pct != null && (
              <div className="text-[10px] text-gray-500 mt-0.5">
                Area YoY {data.yoy_pct >= 0 ? '+' : ''}{data.yoy_pct}%
              </div>
            )}
          </div>
        </div>

        {/* Data quality attribution — auto-mirrors per market via useMarket(). */}
        <div className="mt-3" data-testid="honest-read-data-quality">
          <DataQualityBadge market={market.code} />
        </div>

        {/* Fair offer suggestion */}
        {data.fair_offer && data.delta_pct != null && data.delta_pct > 5 && (
          <div
            className="mt-5 pt-5 border-t border-white/60 flex items-center justify-between gap-3 flex-wrap"
            data-testid="honest-read-fair-offer-block"
          >
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1">
                A starting offer to consider
              </div>
              <div className="text-2xl font-extrabold text-gray-900 tabular-nums" data-testid="honest-read-fair-offer">
                {moneyK(data.fair_offer)}
              </div>
              <div className="text-[11px] text-gray-600 mt-0.5">
                65% weight on median sale + 35% on ask. Use as an anchor, not a ceiling.
              </div>
            </div>
            <Link
              to={data.area_page_url}
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#5b35a8] hover:text-[#7042cc]"
              data-testid="honest-read-area-link"
            >
              See {data.routing_key} prices <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        {/* Footnote when verdict is good / in-line */}
        {(data.verdict === 'in_line' || data.verdict === 'below_market') && (
          <div
            className="mt-5 pt-4 border-t border-white/60 flex items-center justify-between gap-3 flex-wrap"
            data-testid="honest-read-good-footer"
          >
            <div className="flex items-center gap-2 text-sm text-emerald-900 font-medium">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              This one's worth a closer look.
            </div>
            <Link
              to={data.area_page_url}
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#5b35a8] hover:text-[#7042cc]"
              data-testid="honest-read-area-link"
            >
              See {data.routing_key} prices <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        {/* Price-watch CTA — shown on every verdict; especially valuable for
            stretch/above-market where the buyer just realised the asking
            price is unrealistic. */}
        <PriceWatchInline propertyId={data.property_id} />
      </CardContent>
    </Card>
  );
};

export default HonestReadPanel;
;
